package ai.wagemix.logic.picker

import ai.wagemix.net.models.WMChartRules
import ai.wagemix.data.create
import ai.wagemix.data.isOwnedBy
import ai.wagemix.data.WMCache
import ai.wagemix.data.WMStatsSyncEvent
import ai.wagemix.logic.picker.WMOccupationPickerPresenter.Action
import ai.wagemix.logic.picker.WMOccupationPickerPresenter.Event
import ai.wagemix.logic.mixbuilder.adding
import ai.wagemix.logic.mixbuilder.removing
import ai.wagemix.logic.session
import ai.wagemix.net.models.asTree
import ai.wagemix.net.models.WMOccupationTaxonomy
import com.fsryan.foundation.data.wasCachedWithin
import com.fsryan.foundation.logic.FSBasePresenter
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.milliseconds
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.flow.onEach

/** One visible row of the taxonomy tree, whole or pruned to what the search keeps. */
interface WMOccupationPickerRow {
    val occupationId: Int
    val name: String
    /** 0 for the root's children. */
    val depth: Int
    /**
     * The number of leaf occupations under this group; 0 for a leaf. Without a search it counts
     * every descendant occupation; while a search is active ([countsMatches]) it counts only the
     * descendant occupations the search keeps (those whose name matches, or that sit under a
     * group whose name matches).
     */
    val descendantCount: Int
    /** `true` while a search is active, so [descendantCount] counts matches, not totals. */
    val countsMatches: Boolean
    /** The row is a group with visible children, so it can expand. */
    val hasChildren: Boolean
    val expanded: Boolean
    /** Already part of the draft. */
    val inMix: Boolean
    /** An ancestor is selected, so this one is covered. */
    val parentSelected: Boolean
    val selected: Boolean

    companion object
}

fun WMOccupationPickerRow(
    occupationId: Int,
    name: String,
    depth: Int,
    descendantCount: Int,
    countsMatches: Boolean,
    hasChildren: Boolean,
    expanded: Boolean,
    inMix: Boolean,
    parentSelected: Boolean,
    selected: Boolean,
): WMOccupationPickerRow {
    return WMOccupationPickerRowData(
        occupationId = occupationId,
        name = name,
        depth = depth,
        descendantCount = descendantCount,
        countsMatches = countsMatches,
        hasChildren = hasChildren,
        expanded = expanded,
        inMix = inMix,
        parentSelected = parentSelected,
        selected = selected,
    )
}

private data class WMOccupationPickerRowData(
    override val occupationId: Int,
    override val name: String,
    override val depth: Int,
    override val descendantCount: Int,
    override val countsMatches: Boolean,
    override val hasChildren: Boolean,
    override val expanded: Boolean,
    override val inMix: Boolean,
    override val parentSelected: Boolean,
    override val selected: Boolean,
): WMOccupationPickerRow

/**
 * The occupation picker (side sheet on desktop, bottom sheet on mobile): search the taxonomy,
 * expand the tree, select, then add the selection to the draft. Selection and expansion live in
 * the local context so the presenter holds no state.
 */
/** Adding the selection would take the mix to [attempted] occupations, past the [limit] the API charts. */
@ConsistentCopyVisibility
data class WMOccupationLimit internal constructor(val limit: Int, val attempted: Int) {
    companion object
}

fun WMOccupationLimit.Companion.of(limit: Int, attempted: Int): WMOccupationLimit = WMOccupationLimit(limit, attempted)

interface WMOccupationPickerPresenter<out E: Event, in A: Action>: FSPresenter<E, A> {

    interface Action: FSPresenter.Action {
        sealed interface Base: Action

        @ConsistentCopyVisibility
        data class SetQuery internal constructor(val query: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SetQuery> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ToggleExpanded internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ToggleExpanded> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ToggleSelected internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ToggleSelected> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ClearSelection internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ClearSelection> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class AddSelection internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<AddSelection> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<out A: Action>: FSPresenter.Action.Factory<A> {
            fun createSetQuery(query: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createToggleExpanded(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createToggleSelected(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createClearSelection(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createAddSelection(extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createSetQuery(query: String, extendedProperties: Map<String, Any>): Base = SetQuery(query, extendedProperties)
                override fun createToggleExpanded(occupationId: Int, extendedProperties: Map<String, Any>): Base = ToggleExpanded(occupationId, extendedProperties)
                override fun createToggleSelected(occupationId: Int, extendedProperties: Map<String, Any>): Base = ToggleSelected(occupationId, extendedProperties)
                override fun createClearSelection(extendedProperties: Map<String, Any>): Base = ClearSelection(extendedProperties)
                override fun createAddSelection(extendedProperties: Map<String, Any>): Base = AddSelection(extendedProperties)
            }
        }
    }

    interface Event: FSPresenter.Event {
        /**
         * The rows to render: the taxonomy tree through the expanded groups, pruned to what the
         * search keeps while one is active. Each group carries its leaf-occupation count (totals
         * without a search, matches with one; see [WMOccupationPickerRow.descendantCount]).
         */
        val rowsUpdate: List<WMOccupationPickerRow>?
        val queryUpdate: String?
        val selectionCountUpdate: Int?
        /** `true` when the selection has been added to the draft and the sheet should close. */
        val doneUpdate: Boolean?
        /** `true` when the taxonomy could not be loaded. */
        val taxonomyErrorUpdate: Boolean?
        /**
         * The selection was not added: the mix would have more occupations than [WMChartRules.MAX_OCCUPATIONS]. The
         * selection and the draft are unchanged.
         */
        val occupationLimitUpdate: WMOccupationLimit?

        @ConsistentCopyVisibility
        data class Base internal constructor(
            private val base: FSPresenter.Event.Base,
            override val rowsUpdate: List<WMOccupationPickerRow>? = null,
            override val queryUpdate: String? = null,
            override val selectionCountUpdate: Int? = null,
            override val doneUpdate: Boolean? = null,
            override val taxonomyErrorUpdate: Boolean? = null,
            override val occupationLimitUpdate: WMOccupationLimit? = null
        ): Event, FSPropertyContainer<Base> {
            override val loadingUpdate: Boolean? get() = base.loadingUpdate
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<out E: Event>: FSPresenter.Event.Factory<E> {
            fun createRowsUpdate(rows: List<WMOccupationPickerRow>, query: String, selectionCount: Int): E
            fun createDone(): E
            fun createTaxonomyError(error: Boolean): E
            fun createOccupationLimitUpdate(limit: WMOccupationLimit): E
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                private val base = FSPresenter.Event.baseFactory()
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createRowsUpdate(rows: List<WMOccupationPickerRow>, query: String, selectionCount: Int) =
                    Base(base.none(), rowsUpdate = rows, queryUpdate = query, selectionCountUpdate = selectionCount)
                override fun createDone() = Base(base.none(), doneUpdate = true)
                override fun createTaxonomyError(error: Boolean) = Base(base.none(), taxonomyErrorUpdate = error)
                override fun createOccupationLimitUpdate(limit: WMOccupationLimit) = Base(base.none(), occupationLimitUpdate = limit)
            }
        }
    }

    /** Filters the tree; a selection the new search still keeps has its ancestors expanded. */
    fun userChangedQuery(query: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userToggledExpanded(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userToggledSelected(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userClearedSelection(extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedAddSelection(extendedProperties: Map<String, Any> = emptyMap())

    companion object {
        /** A shorter search text shows the whole tree. */
        const val MIN_QUERY_LENGTH = 2
    }
}

abstract class WMBaseOccupationPickerPresenter<
    E: Event,
    A: Action,
    EF: Event.Factory<E>,
    AF: Action.Factory<A>,
    G: WMOccupationPickerDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    protected val actionFactory: AF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String
): FSBasePresenter<E, A, EF>(cancelViewScopeOnUnready, instanceId, eventFactory), WMOccupationPickerPresenter<E, A> {

    override fun userChangedQuery(query: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSetQuery(query, extendedProperties))
    override fun userToggledExpanded(occupationId: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createToggleExpanded(occupationId, extendedProperties))
    override fun userToggledSelected(occupationId: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createToggleSelected(occupationId, extendedProperties))
    override fun userClearedSelection(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createClearSelection(extendedProperties))
    override fun userRequestedAddSelection(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createAddSelection(extendedProperties))

    @OptIn(FlowPreview::class)
    override fun registerSubscriptions() {
        dataGateway.statsSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMStatsSyncEvent.Taxonomy.Success -> {
                        eventFactory.createLoadingChange(false).emit()
                        eventFactory.createTaxonomyError(false).emit()
                        emitRows(query = "")
                    }
                    is WMStatsSyncEvent.Taxonomy.Failure -> {
                        eventFactory.createLoadingChange(false).emit()
                        eventFactory.createTaxonomyError(true).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()

        // `occupation_searched` once a search settles, never per keystroke: the rows this presenter emits
        // carry the query, so the settled value is the last one emitted before a pause.
        eventFlow
            .mapNotNull { event -> event.queryUpdate?.trim() }
            .debounce(SEARCH_SETTLE_DELAY)
            .distinctUntilChanged()
            .onEach { query -> logSearchIfNeeded(query) }
            .launchInViewScope()
    }

    private suspend fun logSearchIfNeeded(query: String) {
        val needle = pickerNeedleOf(query) ?: return
        val taxonomy = dataGateway.retrieveTaxonomy()?.data ?: return
        FSLog.sendWMOccupationSearched(query = query, resultCount = taxonomy.searchResultCount(needle))
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        if (isFirstOnReadyCall) {
            val uid = dataGateway.session()?.uid
            val count = dataGateway.retrieveMixDraft()?.takeIf { it.isOwnedBy(uid) }?.occupations?.size ?: 0
            FSLog.sendWMOccupationPickerOpened(occupationCount = count)
        }
        val cached = dataGateway.retrieveTaxonomy()
        if (cached == null || !cached.wasCachedWithin(TAXONOMY_TTL)) {
            eventFactory.createLoadingChange(true).emit()
            dataGateway.syncTaxonomy(additionalContext = createPresenterContext())
        }
        if (cached != null) {
            emitRows(query = "")
        }
    }

    override fun onUserAction(action: A) {
        logUserAction(action)
        when (action) {
            is Action.SetQuery -> launch { handleSetQuery(action) }
            is Action.ToggleExpanded -> launch {
                val uid = dataGateway.session()?.uid
                val expanded = dataGateway.retrieveUserContext(uid).pickerExpansion()
                val next = if (action.occupationId in expanded) expanded - action.occupationId else expanded + action.occupationId
                dataGateway.upsertPickerExpansion(uid, next)
                emitRows(query = currentQuery(action), expanded = next)
            }
            is Action.ToggleSelected -> launch {
                val uid = dataGateway.session()?.uid
                val selected = dataGateway.retrieveUserContext(uid).pickerSelection()
                val next = if (action.occupationId in selected) selected - action.occupationId else selected + action.occupationId
                dataGateway.upsertPickerSelection(uid, next)
                emitRows(query = currentQuery(action))
            }
            is Action.ClearSelection -> launch {
                dataGateway.deletePickerSelection(dataGateway.session()?.uid)
                emitRows(query = currentQuery(action))
            }
            is Action.AddSelection -> launch { handleAddSelection() }
            else -> logUnknownActionType(action)
        }
    }

    /**
     * A new search text: when a selected occupation or group is still kept by the new search (or
     * there is no search), its ancestors are expanded and stored so the selection stays visible.
     */
    private suspend fun handleSetQuery(action: Action.SetQuery) {
        val taxonomy = dataGateway.retrieveTaxonomy()?.data ?: return
        val uid = dataGateway.session()?.uid
        val context = dataGateway.retrieveUserContext(uid)
        val expanded = context.pickerExpansion()
        val revealed = taxonomy.expansionRevealing(context.pickerSelection(), pickerNeedleOf(action.query), expanded)
        if (revealed != expanded) {
            dataGateway.upsertPickerExpansion(uid, revealed)
        }
        emitRows(query = action.query, expanded = revealed)
    }

    /** The query rides on the action's extended properties under [KEY_QUERY]; absent means the tree view. */
    private fun currentQuery(action: FSPropertyContainer<*>): String = action.extendedProperties[KEY_QUERY] as? String ?: ""

    private suspend fun handleAddSelection() {
        val uid = dataGateway.session()?.uid
        val selected = dataGateway.retrieveUserContext(uid).pickerSelection()
        if (selected.isEmpty()) {
            return
        }
        val taxonomy = dataGateway.retrieveTaxonomy()?.data
        // Selecting a parent covers its descendants; only the top-most selected nodes are added.
        val toAdd = taxonomy?.let { t -> selected.filter { id -> t.coveringIdsOf(id).none { it in selected } } } ?: selected.toList()
        // An added node replaces what overlaps it in the mix: the groups above it (and "All occupations"), and the
        // occupations below it. Keeping both would count the same wages twice.
        val above = taxonomy?.let { t -> toAdd.flatMap { t.coveringIdsOf(it) }.toSet() } ?: emptySet()
        val overlaps: (Int) -> Boolean = { id ->
            id !in toAdd && (id in above || taxonomy?.coveringIdsOf(id)?.any { it in toAdd } == true)
        }
        val addition: (WMCache.Value.MixDraft?) -> WMCache.Value.MixDraft = { current ->
            // A draft written for someone else is absent for this user.
            var draft = current?.takeIf { it.isOwnedBy(uid) }
                ?: WMCache.Value.MixDraft.create(emptyList(), ownerUid = uid)
            toAdd.forEach { id -> if (draft.occupations.none { it.id == id }) draft = draft.adding(id) }
            draft.occupations.map { it.id }.filter(overlaps).forEach { id -> draft = draft.removing(id) }
            draft
        }
        val before = dataGateway.retrieveMixDraft()
        // Past the limit the API refuses the mix's chart (WMChartRules), so nothing is added and the selection stays.
        val attempted = addition(before).occupations.size
        if (attempted > WMChartRules.MAX_OCCUPATIONS) {
            eventFactory.createOccupationLimitUpdate(WMOccupationLimit(WMChartRules.MAX_OCCUPATIONS, attempted)).emit()
            return
        }
        dataGateway.modifyMixDraft(addition)
        dataGateway.deletePickerSelection(uid)
        val ownBefore = before?.takeIf { it.isOwnedBy(uid) }
        logOccupationsAdded(before = ownBefore, after = addition(before), taxonomy = taxonomy)
        eventFactory.createDone().emit()
    }

    /** `occupations_added` from the draft before and after the addition; a group has occupations under it. */
    private fun logOccupationsAdded(
        before: WMCache.Value.MixDraft?,
        after: WMCache.Value.MixDraft,
        taxonomy: WMOccupationTaxonomy?,
    ) {
        val beforeIds = before?.occupations?.map { it.id }?.toSet() ?: emptySet()
        val afterIds = after.occupations.map { it.id }.toSet()
        val added = afterIds - beforeIds
        val groups = taxonomy?.asTree()?.leafCounts()?.keys ?: emptySet()
        val groupsAdded = added.count { it in groups }
        FSLog.sendWMOccupationsAdded(
            addedCount = added.size,
            groupsAdded = groupsAdded,
            leavesAdded = added.size - groupsAdded,
            replacedCount = (beforeIds - afterIds).size,
            occupationCount = afterIds.size,
        )
    }

    /** Emits the rows for [query]; [expanded] overrides the stored expansion just written. */
    private suspend fun emitRows(query: String, expanded: Set<Int>? = null) {
        val taxonomy = dataGateway.retrieveTaxonomy()?.data ?: return
        val uid = dataGateway.session()?.uid
        val context = dataGateway.retrieveUserContext(uid)
        val selected = context.pickerSelection()
        val draft = dataGateway.retrieveMixDraft()?.takeIf { it.isOwnedBy(uid) }
        val inMix = draft?.occupations?.map { it.id }?.toSet() ?: emptySet()
        val rows = taxonomy.pickerRows(
            needle = pickerNeedleOf(query),
            expanded = expanded ?: context.pickerExpansion(),
            selected = selected,
            inMix = inMix,
        )
        eventFactory.createRowsUpdate(rows, query, selected.size).emit()
    }

    companion object {
        /** Extended-property key the view model sets so selection changes re-render the right view. */
        const val KEY_QUERY = "ai.wagemix.logic.picker.QUERY"
        val TAXONOMY_TTL = 24.hours
        /** How long the search text must stay unchanged before `occupation_searched` is sent. */
        internal val SEARCH_SETTLE_DELAY = 800.milliseconds
    }
}

typealias WMDefaultOccupationPickerPresenter = WMOccupationPickerPresenter<Event.Base, Action.Base>

fun WMOccupationPickerPresenter.Companion.createDefault(
    dataGateway: WMOccupationPickerDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMOccupationPickerPresenter")
): WMDefaultOccupationPickerPresenter = object: WMBaseOccupationPickerPresenter<Event.Base, Action.Base, Event.Factory<Event.Base>, Action.Factory<Action.Base>, WMOccupationPickerDataGateway>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}
